import React, { useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const RAIL_EXPANDED_WIDTH = 220;
const RAIL_COLLAPSED_WIDTH = 64;

export interface DashboardRailItem {
  id: string;
  icon: string;
  label: string;
  /** True while this item's own destination is the active screen (Home) or
   * the slide-over it opens (Contacts) is open — draws the active pill. */
  active?: boolean;
  onPress: () => void;
}

export interface DashboardRailProps {
  items: DashboardRailItem[];
  /** Bottom-pinned items (profile/settings) — visually separated from the
   * main nav list by the flex spacer, not part of `items`. */
  footerItems: DashboardRailItem[];
  collapsed: boolean;
  /** Hidden entirely on mobile widths — collapse is forced, not user-togglable there. */
  showCollapseToggle: boolean;
  onToggleCollapse: () => void;
}

/** Vertical, iconized, collapsible left navigation rail for the new dashboard
 * screen — replaces the old top Header + LeftPane concept for this screen
 * only (see DashboardScreen.tsx). Width animates via Animated.timing (not
 * native-driver-eligible, width is a layout property); row labels fade out
 * via an opacity interpolation off the same value so they never visibly wrap
 * mid-animation. */
export const DashboardRail: React.FC<DashboardRailProps> = ({
  items,
  footerItems,
  collapsed,
  showCollapseToggle,
  onToggleCollapse,
}) => {
  const styles = useThemedStyles(getStyles);
  const widthAnim = useRef(new Animated.Value(collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_EXPANDED_WIDTH)).current;

  useEffect(() => {
    Animated.timing(widthAnim, {
      toValue: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_EXPANDED_WIDTH,
      duration: 200,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [collapsed, widthAnim]);

  // Labels fade out over the first half of the collapse animation so they
  // never render mid-collapse at an awkward partial width.
  const labelOpacity = widthAnim.interpolate({
    inputRange: [RAIL_COLLAPSED_WIDTH, RAIL_COLLAPSED_WIDTH + (RAIL_EXPANDED_WIDTH - RAIL_COLLAPSED_WIDTH) / 2, RAIL_EXPANDED_WIDTH],
    outputRange: [0, 0, 1],
    extrapolate: 'clamp',
  });

  const renderItem = (item: DashboardRailItem) => (
    <TouchableOpacity
      key={item.id}
      style={[styles.item, item.active && styles.itemActive]}
      onPress={item.onPress}
      activeOpacity={0.75}
    >
      <Ionicons name={item.icon as any} size={20} color={item.active ? colors.brand.primary : (colors.text.secondary as string)} />
      <Animated.Text
        style={[styles.itemLabel, item.active && styles.itemLabelActive, { opacity: labelOpacity }]}
        numberOfLines={1}
      >
        {item.label}
      </Animated.Text>
    </TouchableOpacity>
  );

  return (
    <Animated.View style={[styles.container, { width: widthAnim }]}>
      <View style={styles.list}>{items.map(renderItem)}</View>
      <View style={styles.spacer} />
      <View style={styles.footer}>
        {showCollapseToggle && (
          <TouchableOpacity style={styles.item} onPress={onToggleCollapse} activeOpacity={0.75}>
            <DirectionalIcon name={collapsed ? 'chevron-forward' : 'chevron-back'} size={20} color={colors.text.secondary as string} />
            <Animated.Text style={[styles.itemLabel, { opacity: labelOpacity }]} numberOfLines={1}>
              Collapse
            </Animated.Text>
          </TouchableOpacity>
        )}
        {footerItems.map(renderItem)}
      </View>
    </Animated.View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    height: '100%',
    backgroundColor: colors.background.surface,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    paddingVertical: designTokens.spacing.md,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { flexShrink: 0 }),
  },
  list: {
    gap: 2,
  },
  spacer: {
    flex: 1,
  },
  footer: {
    gap: 2,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    marginHorizontal: designTokens.spacing.sm,
    borderRadius: designTokens.borderRadius.md,
  },
  itemActive: {
    backgroundColor: colors.primaryTransparent,
  },
  itemLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    flexShrink: 1,
  },
  itemLabelActive: {
    color: colors.brand.primary,
    fontFamily: typography.fontFamily.semibold,
  },
});
